import React, { useState } from 'react';
import { faCheck, faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';

import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';
import { Button } from 'shared/components/display';

import { BUTTON_VARIANTS } from 'shared/styles/button';
import { copyToClipboard } from 'shared/utilities/stringUtility';

import styles from './styles';

const COPIED_FEEDBACK_MS = 1500;

interface Props {
  /** Value to copy to clipboard. */
  value: string;
  /** Tooltip text shown before copy. Defaults to "Copy". */
  tooltip?: string;
  /** Tooltip text shown briefly after copy. Defaults to "Copied!". */
  copiedTooltip?: string;
  /** Optional aria-label / data-cy hint, e.g. "creator-id". */
  dataCy?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'lg';
  /**
   * Optional label rendered next to the icon (e.g. "Copy row"). When provided,
   * the button stays visible at all times instead of fading in on parent hover —
   * label-bearing buttons are typically primary actions, not hover affordances.
   */
  children?: React.ReactNode;
}

/**
 * Click-to-copy button. Two modes:
 *   - Icon-only (default): tiny copy icon that fades in on parent-cell hover —
 *     intended as an inline affordance next to a value (creator ID, amount, etc.).
 *   - Icon + label: pass a `children` label (e.g. "Copy row"). The button is then
 *     always visible and behaves as a discrete action button.
 *
 * Both modes copy `value` to the clipboard on click and briefly switch the icon
 * to a check + "Copied!" tooltip for feedback.
 */
const CopyButton: React.FC<Props> = ({
  value,
  tooltip = 'Copy',
  copiedTooltip = 'Copied!',
  dataCy,
  className,
  size = 'xs',
  children,
}) => {
  const [copied, setCopied] = useState(false);
  const hasLabel = children !== undefined && children !== null && children !== false;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <Tooltip content={copied ? copiedTooltip : tooltip}>
      <Button
        css={styles}
        className={cs('copy-button', className, { 'copy-button--with-label': hasLabel })}
        onClick={handleClick}
        variant={BUTTON_VARIANTS.TEXT_ONLY}
        data-cy={dataCy ? `copy-button__${dataCy}` : undefined}
        type="button"
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} size={size} />
        {hasLabel ? <span className="copy-button__label">{children}</span> : null}
      </Button>
    </Tooltip>
  );
};

export default CopyButton;
